'use no memo';

import type { PostPublic } from '@repo/types/posts';
import type { UserPublic } from '@repo/types/users';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';

import { useGetPosts } from '@repo/api/post/useGetPosts';
import { format, isValid, parseISO, startOfDay, endOfDay } from 'date-fns';
import { EditIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';

import ActivatePost from '@/components/features/manage/manage-posts/ActivatePost';
import CreatePost from '@/components/features/manage/manage-posts/CreatePost';
import DeletePost from '@/components/features/manage/manage-posts/DeletePost';
import EditPost from '@/components/features/manage/manage-posts/EditPost';
import TanstackTable from '@/components/features/manage/tanstack-table/TanstackTable';
import { useTanstackTable } from '@/components/features/manage/tanstack-table/useTanstackTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTableRowSelection } from '@/hooks/table';

export default function ManagePostsPage() {
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openChangeActive, setOpenChangeActive] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openPostAdd, setOpenPostAdd] = useState<boolean>(false);

  const [activePost, setActivePost] = useState<PostPublic | undefined>(undefined);

  const [rowSelection, setRowsSelection] = useState<RowSelectionState>({});
  const { data = [], isPending } = useGetPosts();

  const columns = useMemo<ColumnDef<PostPublic>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label={'Select row'} />,
        enableSorting: false,
        enableHiding: false,
      },
      {
        meta: 'Tytuł',
        id: 'name',
        accessorKey: 'name',
        header: 'Tytuł',
      },
      {
        meta: 'Twórca',
        id: 'creator',
        accessorKey: 'creator',
        accessorFn: (row) => row.creator,
        cell: ({ getValue }) => {
          const creator = getValue() as UserPublic | null;
          if (creator) {
            return creator.name + ' ' + creator.surname;
          }
          return '';
        },
        header: 'Twórca',
      },
      {
        meta: 'Aktywność',
        id: 'active',
        accessorKey: 'active',
        cell: ({ getValue }) => {
          const active = getValue();
          if (active === true) {
            return <Badge variant={'success'}>Aktywny</Badge>;
          }
          if (active === false) {
            return <Badge variant={'destructive'}>Nieaktywny</Badge>;
          }
          return '';
        },
        header: 'Aktywność',
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        meta: 'Ostatnie zmiany',
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }
          return date;
        },
        header: 'Ostatnie zmiany',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Data utworzenia',
        id: 'createdAt',
        accessorKey: 'createdAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy');
          }
          return date;
        },
        header: 'Data utworzenia',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        meta: 'Data publikacji',
        id: 'publishedAt',
        accessorKey: 'publishedAt',
        sortType: 'datetime',
        cell: ({ getValue }) => {
          const date = getValue();
          if (typeof date === 'string' && isValid(parseISO(date))) {
            return format(new Date(date), 'dd.MM.yyyy, HH:mm');
          }
          return date;
        },
        header: 'Data publikacji',
        filterFn: (row, id, value) => {
          if (!value || (!value.start && !value.end)) return true;
          const dateStr = row.getValue(id) as string;
          if (!dateStr || !isValid(parseISO(dateStr))) return false;
          const date = startOfDay(parseISO(dateStr));
          const start = value.start ? startOfDay(new Date(value.start)) : null;
          const end = value.end ? endOfDay(new Date(value.end)) : null;
          if (start && end) {
            return date >= start && date <= end;
          }
          if (start) {
            return date >= start;
          }
          if (end) {
            return date <= end;
          }
          return true;
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowOrigin = row.original;
          return (
            <div className={'flex justify-end'}>
              <Button
                size={'sm'}
                variant={'ghost'}
                variantColor={'destructive'}
                onClick={() => {
                  setActivePost(rowOrigin);
                  setOpenDelete(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size={'sm'} variant={'ghost'}>
                    <EditIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={'end'} className="w-full max-w-xs">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        setActivePost(rowOrigin);
                        setOpenEdit(true);
                      }}
                    >
                      <div className="space-y-0.5">
                        <p>Edytuj</p>
                        <p className="text-text-tertiary ms-1 text-xs">Edytuj dane postu</p>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        setActivePost(rowOrigin);
                        setOpenChangeActive(true);
                      }}
                    >
                      {rowOrigin.active ? (
                        <div className="space-y-0.5">
                          <p>Dezaktywuj</p>
                          <p className="text-text-tertiary ms-1 text-xs">Dezaktywuj post, post nie będzie widoczny dla użytkowników</p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <p>Aktywuj</p>
                          <p className="text-text-tertiary ms-1 text-xs">Aktywuj post, post będzie widoczny dla użytkowników</p>
                        </div>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  const { table, setGlobalFilter } = useTanstackTable({
    name: 'posts',
    columns,
    data,
    getRowId: (row) => String(row.id),
    rowSelection: rowSelection,
    onRowSelectionChange: setRowsSelection,

    defaultColumnVisibility: {
      updatedAt: false,
      createdAt: false,
    },
  });

  const selectedPosts = useMemo<PostPublic[]>(() => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return [];
    const idSet = new Set(selectedIds.map(Number));
    return data.filter((group) => idSet.has(group.id));
  }, [rowSelection, data]);

  const { handleRowClick } = useTableRowSelection({
    table,
    setRowSelection: setRowsSelection,
  });

  const addGroup = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Dodaj post',
        icon: <PlusIcon className={'size-4'} />,
        func: () => setOpenPostAdd(true),
      },
    ];
  }, []);

  return (
    <>
      <CreatePost open={openPostAdd} onOpenChange={setOpenPostAdd} />
      <EditPost
        post={activePost || selectedPosts[0]}
        open={openEdit}
        onOpenChange={(open) => {
          setOpenEdit(open);
          if (!open) setActivePost(undefined);
        }}
      />
      <DeletePost
        posts={activePost ? [activePost] : selectedPosts}
        open={openDelete}
        onOpenChange={(open) => {
          setOpenDelete(open);
          if (!open) setActivePost(undefined);
        }}
      />
      <ActivatePost
        post={activePost || selectedPosts[0]}
        open={openChangeActive}
        onOpenChange={(open) => {
          setOpenChangeActive(open);
          if (!open) setActivePost(undefined);
        }}
      />

      <div className={'base-direction-col h-full'}>
        <div className={'viewport-height flex w-full flex-col gap-4'}>
          <TanstackTable
            table={table}
            label={'Posty'}
            isLoading={isPending}
            onGlobalFilterChange={setGlobalFilter}
            onRemoveSelectedRows={() => setOpenDelete(true)}
            filterBy={[
              {
                id: 'active',
                title: 'Aktywność',
                type: 'faceted',
                options: [
                  {
                    label: 'Aktywny',
                    value: true,
                  },
                  {
                    label: 'Nieaktywny',
                    value: false,
                  },
                ],
              },
              {
                id: 'createdAt',
                title: 'Data utworzenia',
                type: 'dateRange',
              },
              {
                id: 'updatedAt',
                title: 'Ostatnie zmiany',
                type: 'dateRange',
              },
            ]}
            addGroup={addGroup}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </>
  );
}
